// components/ui/Skeleton.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  DimensionValue,
  Easing,
  LayoutChangeEvent,
  Platform,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { useTheme } from "@/hooks/useTheme";

/** Base rectangle with shimmer */
export function Skeleton({
  width = 100,
  height = 14,
  radius = 8,
  style,
  shimmer = true,
  duration = 1300,
}: {
  width?: DimensionValue;
  height?: number;
  radius?: number;
  style?: StyleProp<ViewStyle>;
  shimmer?: boolean;
  duration?: number;
}) {
  const { colors } = useTheme();
  const [w, setW] = useState<number>(0);
  const translate = useRef(new Animated.Value(-150)).current;

  // neutral base & shimmer highlight derived from theme
  const baseBg = colors.backgroundHover;
  const hi = useMemo(() => {
    // faint accent tint for a tiny bit of life
    const isLight = colors.tint === "light";
    const accent = isLight ? "#FFFFFF" : (colors.indigo?.background ?? colors.primary ?? "#999");
    const shimmerAlpha = isLight ? 0.5 : 0.06;
    return [toAlpha(baseBg, 0.0), toAlpha(accent, shimmerAlpha), toAlpha(baseBg, 0.0)];
  }, [colors, baseBg]);

  useEffect(() => {
    if (!shimmer) return;
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      translate.setValue(-150);
      Animated.timing(translate, {
        toValue: (w || 300) + 150,
        duration,
        easing: Easing.inOut(Easing.linear),
        useNativeDriver: Platform.OS !== "web",
      }).start(({ finished }) => {
        if (finished && !cancelled) run();
      });
    };
    run();
    return () => {
      cancelled = true;
      translate.stopAnimation();
    };
  }, [w, duration, shimmer, translate]);

  const onLayout = (e: LayoutChangeEvent) => setW(e.nativeEvent.layout.width);

  return (
    <View
      onLayout={onLayout}
      style={[
        { width, height, borderRadius: radius, overflow: "hidden", backgroundColor: baseBg },
        Platform.OS === "web" && { willChange: "transform" as any },
        style,
      ]}
    >
      {shimmer && (
        <Animated.View
          style={[
            StyleSheet.absoluteFill,
            {
              transform: [{ translateX: translate }],
            },
          ]}
          pointerEvents="none"
        >
          <LinearGradient
            colors={hi as [string, string, ...string[]]}
            start={{ x: 0, y: 0.1 }}
            end={{ x: 1, y: 0.9 }}
            style={{ width: 150, ...StyleSheet.absoluteFillObject }}
          />
        </Animated.View>
      )}
    </View>
  );
}

/** Circle avatar placeholder */
export function SkeletonCircle({
  size = 40,
  style,
  ...rest
}: { size?: number; style?: StyleProp<ViewStyle> } & Omit<
  React.ComponentProps<typeof Skeleton>,
  "width" | "height" | "radius"
>) {
  return <Skeleton width={size} height={size} radius={size / 2} style={style} {...rest} />;
}

/** Text block (n lines) with varied widths */
export function SkeletonText({
  lines = 3,
  lineHeight = 12,
  gap = 8,
  widths,
  style,
  shimmer = true,
}: {
  lines?: number;
  lineHeight?: number;
  gap?: number;
  widths?: DimensionValue[];
  style?: StyleProp<ViewStyle>;
  shimmer?: boolean;
}) {
  return (
    <View style={[{ gap }, style]}>
      {Array.from({ length: lines }).map((_, i) => {
        const w = widths?.[i] ?? (i === lines - 1 ? 60 : 100);
        return <Skeleton key={i} width={w} height={lineHeight} shimmer={shimmer} />;
      })}
    </View>
  );
}

/** Row helper: avatar + text lines */
export function SkeletonRow({
  avatar = true,
  avatarSize = 40,
  lines = 2,
  lineHeight,
  style,
  shimmer = true,
  widths,
}: {
  avatar?: boolean;
  avatarSize?: number;
  lines?: number;
  lineHeight?: number;
  widths?: DimensionValue[];
  style?: StyleProp<ViewStyle>;
  shimmer?: boolean;
}) {
  return (
    <View style={[styles.row, style]}>
      {avatar && <SkeletonCircle size={avatarSize} shimmer={shimmer} />}
      <View style={{ flex: 1, gap: 8 }}>
        <SkeletonText lineHeight={lineHeight} lines={lines} shimmer={shimmer} widths={widths} />
      </View>
    </View>
  );
}

/** List helper: repeat rows */
export function SkeletonList({
  count = 6,
  style,
  shimmer = true,
}: {
  count?: number;
  style?: StyleProp<ViewStyle>;
  shimmer?: boolean;
}) {
  return (
    <View style={[{ gap: 12 }, style]}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonRow key={i} shimmer={shimmer} />
      ))}
    </View>
  );
}

/** Utility to apply alpha to hex or rgb strings; falls back to rgba black/white */
function toAlpha(c: string, a: number) {
  if (c.startsWith("#")) {
    // expand short hex
    const hex = c.length === 4 ? `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}` : c;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${a})`;
  }
  if (c.startsWith("rgb")) {
    return c.replace("rgb", "rgba").replace(")", `,${a})`);
  }
  // neutral fallback
  return `rgba(255,255,255,${a})`;
}

const styles = StyleSheet.create({
  row: { flexDirection: "row", alignItems: "center", gap: 12 },
});
